<template>
  <div v-loading="loading" class="symptom-question-page">
    <!-- 顶部标题 -->
    <div class="header-section">
      <div class="step-label">Q{{ currentIndex + 1 }}/{{ questionSets.length }}</div>
      <div class="divider" />
      <div class="question-title">
        请选择您的病症表现
        <span v-if="currentSet"> — {{ currentSet.name }}</span>
        <span v-if="diagInfo && diagInfo.chDiagName" class="diag-name"> — {{ diagInfo.chDiagName }}</span>
      </div>
    </div>

    <!-- 选项网格 -->
    <div class="options-grid">
      <div
        v-for="(opt, idx) in currentOptions"
        :key="idx"
        class="option-btn"
        :class="{ active: isSelected(opt) }"
        @click="toggleOption(opt)"
      >
        <div class="dot" :class="{ active: isSelected(opt) }" />
        <span>{{ opt }}</span>
      </div>
      <div v-if="currentOptions.length === 0 && !loading" class="empty-tip">
        暂无选项数据
      </div>
    </div>

    <!-- 分页控制 -->
    <div class="footer-actions">
      <div
        class="action-btn btn-nav"
        :class="{ disabled: currentIndex === 0 }"
        @click="prevPage"
      >
        上一页
      </div>
      <div
        class="action-btn btn-nav"
        :class="{ disabled: currentIndex === questionSets.length - 1 }"
        @click="nextPage"
      >
        下一页
      </div>
      <div class="action-btn btn-back" @click="handleBack">返回</div>
      <div class="action-btn btn-confirm" @click="handleConfirm">确定</div>
    </div>
  </div>
</template>

<script>
import { postData } from '@/api/ik/data/dataApi'
import store from '@/store/index'

export default {
  name: 'SymptomQuestionPage',
  props: {
    diagInfo: { type: Object, default: null },
    sets: { type: Array, default: null },
    initialSelected: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      currentIndex: 0,
      selectedMap: {},
      questionSets: [],
      loading: false
    }
  },
  computed: {
    currentSet() {
      return this.questionSets[this.currentIndex] || null
    },
    currentOptions() {
      return this.currentSet ? this.currentSet.options : []
    }
  },
  watch: {
    diagInfo: {
      immediate: true,
      deep: true,
      handler(val, oldVal) {
        if (val && val.chDiagDictionaryCode) {
          if (!oldVal || oldVal.chDiagDictionaryCode !== val.chDiagDictionaryCode) {
            this.fetchSymptomsByDiag()
          }
        }
      }
    },
    sets: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.questionSets = val
        }
      }
    },
    initialSelected: {
      immediate: true,
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.selectedMap = JSON.parse(JSON.stringify(val))
        }
      }
    }
  },
  created() {
    if ((!this.sets || this.sets.length === 0) && !this.diagInfo) {
      this.questionSets = [
        {
          name: '呼吸不适',
          options: [
            '倚息不得卧', '憋气', '喘', '喘促', '喘促、动则加重',
            '短气', '动则气喘', '呼吸气粗', '呼吸微弱', '气促', '气短动则加重'
          ]
        },
        {
          name: '胸痛',
          options: ['刺痛', '胀痛', '隐痛', '绞痛', '灼痛', '闷痛']
        },
        {
          name: '心悸',
          options: ['心慌', '心跳剧烈', '心跳不齐', '心悸不安', '惊悸', '怔忡']
        }
      ]
    }
  },
  methods: {
    async fetchSymptomsByDiag() {
      if (!this.diagInfo) return

      const chDiagDictionaryCode = this.diagInfo.chDiagDictionaryCode || this.diagInfo.code
      if (!chDiagDictionaryCode) {
        this.$message.warning('缺少诊断字典编码，无法获取症状选项')
        return
      }

      this.loading = true
      try {
        // storeCode 从路由 query 传值获取（路由传值），其余参数维持原来源
        const storeCode = this.$route?.query?.storeCode ||
                          this.diagInfo.storeCode ||
                          store.state.user?.storeCode ||
                          store.getters?.['user/getStoreCode'] || ''
        const commonSign = this.diagInfo.commonSign !== undefined ? this.diagInfo.commonSign : 1
        const mainSign = this.diagInfo.mainSign !== undefined ? this.diagInfo.mainSign : 1

        const params = { chDiagDictionaryCode, storeCode, commonSign, mainSign }

        const res = await postData(
          'clinic/clinicdic/clinicdiagdic/clinicsymdic/find/findsymdictionarybychdiag',
          params
        )

        const resData = res && res.data !== undefined ? res.data : res
        this.questionSets = this.transformSymptomData(resData)
        this.currentIndex = 0
      } catch (error) {
        console.error('获取症状数据失败:', error)
        this.$message.error('获取症状数据失败')
      } finally {
        this.loading = false
      }
    },

    transformSymptomData(data) {
      if (!data) return []

      if (Array.isArray(data)) {
        const groupMap = {}

        data.forEach(item => {
          if (!item) return

          const groupCode = item.symDirectoryCode || ('unknown_' + (item.id || Math.random()))
          const groupName = item.symDirectoryLevelThreeName ||
            item.symDirectoryLevelTwoName ||
            item.symDirectoryLevelOneName ||
            '未分类'

          if (!groupMap[groupCode]) {
            groupMap[groupCode] = { name: groupName, options: [] }
          }

          const symName = item.symDictionaryName
          if (symName && !groupMap[groupCode].options.includes(symName)) {
            groupMap[groupCode].options.push(symName)
          }
        })

        return Object.values(groupMap).filter(item => item.options.length > 0)
      }

      if (typeof data === 'object' && !Array.isArray(data)) {
        return Object.keys(data).map(key => ({
          name: key,
          options: Array.isArray(data[key]) ? data[key] : []
        })).filter(item => item.options.length > 0)
      }

      return []
    },

    isSelected(option) {
      const setName = this.currentSet?.name
      if (!setName) return false
      const arr = this.selectedMap[setName] || []
      return arr.includes(option)
    },
    toggleOption(option) {
      const setName = this.currentSet.name
      if (!this.selectedMap[setName]) {
        this.$set(this.selectedMap, setName, [])
      }
      const arr = this.selectedMap[setName]
      const idx = arr.indexOf(option)
      if (idx > -1) {
        arr.splice(idx, 1)
      } else {
        arr.push(option)
      }
    },
    prevPage() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    nextPage() {
      if (this.currentIndex < this.questionSets.length - 1) {
        this.currentIndex++
      }
    },
    handleBack() {
      this.$emit('back')
    },

    // ========== 修改：确定按钮通过 AndroidBridge.sendMessage 传给安卓设备 ==========
    handleConfirm() {
      // 1. 构造 symContent：所有选中的选项拼成一个字符串，空格分割
      const allSelected = []
      Object.values(this.selectedMap).forEach(arr => {
        if (arr && arr.length > 0) {
          allSelected.push(...arr)
        }
      })
      const symContent = allSelected.join(' ')

      if (!symContent.trim()) {
        this.$message.warning('请至少选择一个症状')
        return
      }

      // 2. 中医病名
      const chDiagContent = this.diagInfo?.chDiagName || ''

      // 3. 检查 AndroidBridge 是否存在
      if (typeof window.AndroidBridge === 'undefined' || !window.AndroidBridge) {
        this.$message.error('未检测到安卓桥接对象，请在安卓 WebView 中打开')
        console.error('window.AndroidBridge 未定义')
        return
      }

      // 4. 组装数据并序列化为 JSON 字符串，通过 sendMessage 发送
      const payload = {
        chDiagContent, // 中医病名
        symContent // 主诉词
      }

      try {
        window.AndroidBridge.sendMessage(JSON.stringify(payload))

        // 同时向上层抛出事件，方便 Web 端调试
        this.$emit('confirm', {
          selectedMap: this.selectedMap,
          symContent,
          chDiagContent
        })
      } catch (error) {
        console.error('调用 AndroidBridge.sendMessage 失败:', error)
        this.$message.error('提交失败: ' + error.message)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.symptom-question-page {
  width: 100%;
  min-height: 100vh;
  height: auto;
  background: #0a0e27;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 24px;
  box-sizing: border-box;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 顶部标题区 */
.header-section {
  width: 100%;
  max-width: 640px;
  margin-bottom: 20px;
  flex-shrink: 0;

  .step-label {
    font-size: 18px;
    color: #4a9eff;
    font-weight: 600;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }

  .divider {
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.15);
    margin-bottom: 12px;
  }

  .question-title {
    font-size: 16px;
    color: #ffffff;
    font-style: italic;
    line-height: 1.6;

    span {
      color: #4a9eff;
      font-weight: 600;
      font-style: normal;
    }

    .diag-name {
      color: #ff7d00;
      font-weight: 600;
      font-style: normal;
    }
  }
}

/* 选项网格 */
.options-grid {
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 216px);
  min-height: 120px;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-content: flex-start;
  overflow-y: auto;
  padding-right: 4px;
  margin-bottom: 20px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }

  .empty-tip {
    grid-column: 1 / -1;
    text-align: center;
    color: #5a6380;
    font-size: 14px;
    padding: 40px 0;
  }

  .option-btn {
    position: relative;
    min-height: 56px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    display: flex;
    align-items: center;
    padding: 0 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.25);
      margin-right: 10px;
      flex-shrink: 0;
      transition: all 0.2s ease;
    }

    span {
      font-size: 15px;
      color: #b0b8d4;
      line-height: 1.4;
      word-break: break-all;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.15);
    }

    &:active {
      transform: scale(0.98);
    }

    &.active {
      background: rgba(74, 158, 255, 0.12);
      border-color: #4a9eff;
      box-shadow: 0 0 12px rgba(74, 158, 255, 0.15);

      .dot {
        background: #4a9eff;
        box-shadow: 0 0 6px rgba(74, 158, 255, 0.6);
      }

      span {
        color: #ffffff;
        font-weight: 500;
      }
    }
  }
}

/* 底部按钮 */
.footer-actions {
  width: 100%;
  max-width: 640px;
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  .action-btn {
    height: 44px;
    border-radius: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.25s ease;
    letter-spacing: 1px;
    user-select: none;
    white-space: nowrap;

    &.btn-nav {
      flex: 1;
      border: 1px solid rgba(255, 255, 255, 0.25);
      color: #ffffff;
      background: transparent;

      &:hover:not(.disabled) {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.4);
      }

      &:active:not(.disabled) {
        transform: scale(0.98);
      }

      &.disabled {
        opacity: 0.35;
        cursor: not-allowed;
        border-color: rgba(255, 255, 255, 0.12);
      }
    }

    &.btn-back {
      flex: 1;
      border: 1px solid rgba(255, 255, 255, 0.25);
      color: #ffffff;
      background: transparent;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.4);
      }

      &:active {
        transform: scale(0.98);
      }
    }

    &.btn-confirm {
      flex: 1.2;
      border: 1px solid #4a9eff;
      color: #4a9eff;
      background: rgba(74, 158, 255, 0.1);

      &:hover {
        background: rgba(74, 158, 255, 0.2);
        box-shadow: 0 0 16px rgba(74, 158, 255, 0.25);
      }

      &:active {
        transform: scale(0.98);
      }
    }
  }
}
</style>
